import { baseToApiShape, baseFromApiShape, baseInitNewForm, baseInitEditForm } from '../formHelpers/base';
import { imageShapeCompatibility, parseImageName, quotaShortfall, quotaReason } from './imageCompat';

/**
 * Azure-specific flat → nested API payload.
 * Extends base with Azure fields: disk types, proximity placement, capacity auto-expire,
 * static IP, image resource group, marketplace image references.
 */
export function toApiShape(form) {
    const base = baseToApiShape(form);
    return {
        ...base,
        info: {
            ...base.info,
            // The exact image picked (a managed image or a gallery version);
            // null when a stack was picked and the deploy resolves its newest.
            unifiedImageId: form.serverImageId || null,
            prefix: form.prefix || '',
            marketplace_head_image_id: form.marketplaceHeadImageId || null,
            marketplace_compute_image_id: form.marketplaceComputeImageId || null,
        },
        data: {
            ...base.data,
            image_resource_group_name: form.imageResourceGroupName || '',
            marketplace_image_reference: form.marketplaceImageReference || null,
            server: {
                ...base.data.server,
                // Mirror of info.unifiedImageId, so a pin survives a save.
                image_id: form.serverImageId || null,
                disk_type: form.serverDiskType || '',
                ocpus: form.serverOcpus || 1,
                mem: form.serverMem || 1,
            },
            network: {
                ...base.data.network,
                use_proximity_placement: form.useProximityPlacement || false,
                private_static_ip: form.privateStaticIp || null,
                capacity_reservation_auto_expire: form.capacityReservationAutoExpire ?? true,
                capacity_reservation_expire_hours: form.capacityReservationExpireHours ?? 1,
            },
            node: {
                ...base.data.node,
                disk_type: form.nodeDiskType || '',
            },
        },
    };
}

/**
 * Azure-specific nested → flat form state.
 */
/**
 * Every Azure-specific field read back off a saved cluster.
 *
 * fromApiShape and initEditForm both need this list; keeping it in one place
 * is what stops the two drifting apart when a field is added.
 */
function savedAzureFields(cluster) {
    return {
        // The pin, read from either half of the mirror.
        serverImageId: cluster.info?.unifiedImageId || cluster.data?.server?.image_id || '',

        // Azure info fields
        prefix: cluster.info?.prefix || '',
        marketplaceHeadImageId: cluster.info?.marketplace_head_image_id || null,
        marketplaceComputeImageId: cluster.info?.marketplace_compute_image_id || null,

        // Azure server fields
        serverDiskType: cluster.data?.server?.disk_type || '',
        serverOcpus: cluster.data?.server?.ocpus || 1,
        serverMem: cluster.data?.server?.mem || 1,

        // Azure network fields
        useProximityPlacement: cluster.data?.network?.use_proximity_placement || false,
        privateStaticIp: cluster.data?.network?.private_static_ip || null,
        capacityReservationAutoExpire: cluster.data?.network?.capacity_reservation_auto_expire ?? true,
        capacityReservationExpireHours: cluster.data?.network?.capacity_reservation_expire_hours ?? 1,

        // Azure data fields
        imageResourceGroupName: cluster.data?.image_resource_group_name || '',
        marketplaceImageReference: cluster.data?.marketplace_image_reference || null,
        nodeDiskType: cluster.data?.node?.disk_type || '',
    };
}

export function fromApiShape(cluster) {
    return {
        ...baseFromApiShape(cluster),
        ...savedAzureFields(cluster),
    };
}

/**
 * Azure new cluster defaults.
 */
export function initNewForm(provider, config) {
    return {
        ...baseInitNewForm(provider, config),
        capacityReservationAutoExpire: true,
        capacityReservationExpireHours: 1,
        useProximityPlacement: false,
        serverDiskType: '',
        nodeDiskType: '',
        // Marketplace image references (if marketplace stack)
        marketplaceHeadImageId: config.marketplaceConfig?.marketplace_head_image_id || null,
        marketplaceComputeImageId: config.marketplaceConfig?.marketplace_compute_image_id
            || config.marketplaceConfig?.marketplace_head_image_id || null,
        marketplaceImageReference: config.marketplaceConfig ? {
            publisher: config.marketplaceConfig.publisher || 'paratools-inc',
            offer: config.marketplaceConfig.offer || 'pt-pro-4-e4s-msa-cyclecloud-amd64',
            sku: config.marketplaceConfig.sku || 'paratools-pro-cyclecloud-amd64',
            marketplace_image_version: config.marketplaceConfig.marketplace_image_version || 'latest',
            requires_plan: config.marketplaceConfig.requires_plan || false,
        } : null,
    };
}

/**
 * Azure edit-mode initialization.
 */
export function initEditForm(cluster, config) {
    return {
        ...baseInitEditForm(cluster, config),
        ...savedAzureFields(cluster),
    };
}

/**
 * Check if any instance in the cluster supports proximity placement groups.
 * HB-series, HC-series, ND-series, NC-series, H16r/H16mr.
 */
export function shouldEnableProximityPlacement(form) {
    const check = (id) => {
        if (!id) return false;
        if (id.startsWith('Standard_HB')) return true;
        if (id.startsWith('Standard_HC')) return true;
        if (id.startsWith('Standard_ND')) return true;
        if (id.startsWith('Standard_NC')) return true;
        if (id === 'Standard_H16r' || id === 'Standard_H16mr') return true;
        return false;
    };
    if (check(form?.serverInstanceId)) return true;
    return (form?.nodeGroups || []).some(g => check(g.instance_id));
}

/** A group that will actually provision nodes. */
const groupProvisionsNodes = (g) => Number(g?.count) > 0;

/**
 * Every role the cluster provisions, as the catalog's option-state helpers
 * want them. `self` marks the row currently being decided, so a size dropdown
 * can ask "what would the quota look like if THIS row were that size?".
 */
export function imageRoles(form, selfRole) {
    const roles = [{
        role: 'head node',
        instance_id: form?.serverInstanceId,
        count: 1,
        self: selfRole === 'head',
    }];
    (form?.nodeGroups || []).forEach((g, i) => {
        if (!groupProvisionsNodes(g) || !g.instance_id) return;
        roles.push({
            role: `compute group ${i + 1}`,
            instance_id: g.instance_id,
            count: Number(g.count) || 0,
            self: selfRole === i,
        });
    });
    return roles.filter((r) => r.instance_id || r.self);
}

/**
 * Why a stack has no usable image for this credential and region.
 * `images` is the credential's live image list for the chosen region.
 */
export function stackUnavailableReason(stack, { images, credentialId, regionId, credentialsById = {} }) {
    const data = stack.data || {};
    if (data.region_id && data.region_id !== regionId) return `Built in ${data.region_id}`;
    if (stack.credential_id && stack.credential_id !== credentialId) {
        const name = credentialsById[stack.credential_id];
        return name ? `Built with credential ${name}` : 'Built with another credential';
    }
    const state = stack.info && stack.info.state;
    if (state === 'none' || !state) return 'Never built';
    if (state !== 'success') return `Build ${state}`;
    const legacyOnly = (images || []).some((img) => {
        const parsed = parseImageName(img.name);
        return parsed && parsed.prefix === data.prefix && parsed.kind !== 'heidi';
    });
    if (legacyOnly) return 'Multi-image (legacy) build: rebuild as unified';
    return 'Image deleted from Azure';
}

/**
 * Whether an image row can be picked: disabled, with the reason, when one of
 * the already-chosen sizes cannot boot it. Silent until the image list for
 * this credential + region has arrived.
 */
export function azureImageOptionState(image, { form, catalog, terms, productName }) {
    if (!catalog || !catalog.imagesReady || !image) return { disabled: false, reason: null };
    return catalog.imageOptionState(image, { roles: imageRoles(form), terms, productName });
}

/**
 * Whether a stack row can be picked: it needs an image in this subscription
 * and region, and that image has to boot the chosen sizes.
 */
export function azureStackOptionState(stack, { form, catalog, credentialsById, terms, productName }) {
    if (!catalog || !catalog.imagesReady) return { disabled: false, reason: null };
    const image = catalog.resolveImage(stack.data && stack.data.prefix, 'heidi');
    if (!image) {
        return {
            disabled: true,
            reason: stackUnavailableReason(stack, {
                images: catalog.images, credentialId: form?.credentialId, regionId: form?.regionId, credentialsById,
            }),
        };
    }
    return azureImageOptionState(image, { form, catalog, terms, productName });
}

/**
 * Why the current size choice cannot be saved, or null.
 *
 * Safety net for states the dropdowns cannot prevent - an edit form opened on
 * a cluster whose saved size is no longer available, or a region change that
 * invalidates a choice made before it. Silent until the catalog has arrived,
 * so it can never block on missing data.
 */
export function unavailableShapeReason(form, catalog) {
    if (!catalog || !catalog.catalogReady) return null;
    const roles = imageRoles(form);
    for (const role of roles) {
        const shape = catalog.shapeById(role.instance_id);
        if (shape && shape.available === false) {
            return `a VM size available in ${form.regionId} for ${role.role} (${role.instance_id} is not)`;
        }
    }
    // Quota is a whole-cluster question, so it is asked once.
    const totals = catalog.coresByFamily(roles);
    for (const role of roles) {
        const shape = catalog.shapeById(role.instance_id);
        if (!shape) continue;
        const short = quotaShortfall(catalog.quota, shape, totals[shape.family] || 0);
        if (short) return `enough vCPU quota (${quotaReason(short)})`;
    }
    return null;
}

/**
 * Why the current image choice cannot be saved, or null. Same safety-net role
 * as unavailableShapeReason.
 */
export function imageReason(form, catalog, { terms, productName } = {}) {
    if (!catalog || !catalog.imagesReady) return null;
    const image = form.serverImageId
        ? catalog.imageById(form.serverImageId)
        : catalog.resolveImage(form.serverImageName, 'heidi');

    if (form.serverImageId && !image) {
        return `an image that still exists in ${form.regionId} (the selected one does not)`;
    }
    if (!image) {
        if (!form.serverImageName) return null;
        return `an image for ${form.serverImageName} in ${form.regionId}`;
    }
    for (const role of imageRoles(form)) {
        const shape = catalog.shapeById(role.instance_id);
        if (!shape) continue;
        const verdict = imageShapeCompatibility(image, shape, { terms, productName });
        if (!verdict.compatible) return `${verdict.shapeReason} (${role.role})`;
    }
    return null;
}
